import { useMemo, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, CheckCircle2, Clock3, Copy, GraduationCap, Link2, Plus, User, Users, X } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'

const statusLabels = { pendente: 'Aguardando aprovação', ativa: 'Ativa', rejeitada: 'Não aprovada', suspensa: 'Suspensa' }
const papelLabels = { gestor: 'Gestor', professor: 'Professor', aluno: 'Aluno' }

function InstituicaoPage() {
  const usuario = useAuthStore(state => state.usuario)
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const [instituicaoAdminId, setInstituicaoAdminId] = useState('')
  const [turmaForm, setTurmaForm] = useState({ nome: '', descricao: '', ano_letivo: new Date().getFullYear() })
  const [conviteForm, setConviteForm] = useState({ email: '', papel: 'aluno', turma_id: '' })
  const [participanteForm, setParticipanteForm] = useState({ membro_id: '', turma_id: '' })
  const [ultimoConvite, setUltimoConvite] = useState('')

  const isAdmin = usuario?.tipo_perfil === 'admin'
  const minhasQuery = useQuery({
    queryKey: ['minhas-instituicoes'],
    queryFn: async () => (await api.get('/instituicoes/minhas')).data,
    enabled: Boolean(usuario && !isAdmin)
  })

  const instituicoesAdminQuery = useQuery({
    queryKey: ['admin-instituicoes'],
    queryFn: async () => (await api.get('/admin/instituicoes')).data,
    enabled: Boolean(usuario && isAdmin)
  })

  const vinculos = useMemo(() => minhasQuery.data?.vinculos || [], [minhasQuery.data?.vinculos])
  const vinculoGestor = useMemo(
    () => vinculos.find(item => item.papel === 'gestor' && item.status === 'ativo' && item.instituicao.status === 'ativa'),
    [vinculos]
  )
  const instituicoesAdmin = useMemo(
    () => (instituicoesAdminQuery.data?.instituicoes || []).filter(item => item.status === 'ativa'),
    [instituicoesAdminQuery.data?.instituicoes]
  )
  const instituicaoSolicitada = searchParams.get('instituicao')
  const instituicaoInicialAdmin = instituicoesAdmin.some(item => item.id === instituicaoSolicitada)
    ? instituicaoSolicitada
    : instituicoesAdmin[0]?.id
  const instituicaoId = isAdmin
    ? (instituicaoAdminId || instituicaoInicialAdmin)
    : vinculoGestor?.instituicao?.id
  const podeGerenciar = isAdmin ? Boolean(instituicaoId) : Boolean(vinculoGestor)

  const painelQuery = useQuery({
    queryKey: ['instituicao-painel', instituicaoId],
    queryFn: async () => (await api.get(`/instituicoes/${instituicaoId}/painel`)).data,
    enabled: Boolean(instituicaoId)
  })

  const criarTurma = useMutation({
    mutationFn: payload => api.post(`/instituicoes/${instituicaoId}/turmas`, payload),
    onSuccess: response => {
      toast.success(response.data.message)
      setTurmaForm({ nome: '', descricao: '', ano_letivo: new Date().getFullYear() })
      queryClient.invalidateQueries({ queryKey: ['instituicao-painel', instituicaoId] })
    },
    onError: error => toast.error(getApiError(error))
  })

  const criarConvite = useMutation({
    mutationFn: payload => api.post(`/instituicoes/${instituicaoId}/convites`, payload),
    onSuccess: response => {
      const link = `${window.location.origin}/convite/${response.data.token}`
      setUltimoConvite(link)
      setConviteForm(current => ({ ...current, email: '' }))
      toast.success('Convite criado! Copie o link para compartilhar.')
      queryClient.invalidateQueries({ queryKey: ['instituicao-painel', instituicaoId] })
    },
    onError: error => toast.error(getApiError(error))
  })

  const cancelarConvite = useMutation({
    mutationFn: conviteId => api.patch(`/instituicoes/${instituicaoId}/convites/${conviteId}/cancelar`),
    onSuccess: response => {
      toast.success(response.data.message)
      queryClient.invalidateQueries({ queryKey: ['instituicao-painel', instituicaoId] })
    },
    onError: error => toast.error(getApiError(error))
  })

  const incluirParticipante = useMutation({
    mutationFn: payload => api.post(`/instituicoes/${instituicaoId}/turmas/${payload.turma_id}/membros`, { membro_id: payload.membro_id }),
    onSuccess: response => {
      toast.success(response.data.message)
      setParticipanteForm(current => ({ ...current, membro_id: '' }))
      queryClient.invalidateQueries({ queryKey: ['instituicao-painel', instituicaoId] })
      queryClient.invalidateQueries({ queryKey: ['recomendacoes', 'destinos'] })
    },
    onError: error => toast.error(getApiError(error))
  })

  if (!usuario) return <Navigate to="/login" replace />
  if (!isAdmin && !usuario.instituicoes?.length) return <Navigate to="/" replace />

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(ultimoConvite)
      toast.success('Link copiado!')
    } catch {
      toast.error('Não foi possível copiar. Selecione o link manualmente.')
    }
  }

  return (
    <div className="space-y-7">
      <header>
        <div className="mb-1 flex items-center gap-2"><Building2 size={20} /><h1 className="text-xl font-medium lg:text-2xl">{isAdmin ? 'Instituições' : 'Minha instituição'}</h1></div>
        <p className="text-sm text-gray-500 dark:text-zinc-400">{isAdmin ? 'Escolha uma instituição ativa para gerenciar turmas e convites.' : 'Acompanhe seus vínculos e organize a comunidade educacional.'}</p>
      </header>

      {(minhasQuery.isLoading || instituicoesAdminQuery.isLoading) && <p className="card text-sm text-gray-500">Carregando instituição...</p>}
      {(minhasQuery.isError || instituicoesAdminQuery.isError) && <p className="card text-sm text-red-600">{getApiError(minhasQuery.error || instituicoesAdminQuery.error)}</p>}

      {isAdmin && !instituicoesAdminQuery.isLoading && !instituicoesAdminQuery.isError && (
        instituicoesAdmin.length ? (
          <label className="card block space-y-2">
            <span className="text-sm font-medium">Instituição a gerenciar</span>
            <select
              className="input-field"
              value={instituicaoId || ''}
              onChange={event => {
                setInstituicaoAdminId(event.target.value)
                setConviteForm(current => ({ ...current, turma_id: '' }))
                setParticipanteForm({ membro_id: '', turma_id: '' })
                setUltimoConvite('')
              }}
            >
              {instituicoesAdmin.map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}
            </select>
          </label>
        ) : <p className="card text-sm text-gray-500">Não há instituições ativas para gerenciar.</p>
      )}

      {!isAdmin && <div className="grid gap-4 xl:grid-cols-2">
          {vinculos.map(vinculo => <InstitutionCard key={vinculo.id} vinculo={vinculo} />)}
        </div>}

      {podeGerenciar && (
        <section className="space-y-6 border-t border-gray-100 pt-7 dark:border-zinc-800">
          <div><h2 className="text-lg font-medium">Gestão institucional</h2><p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">Crie turmas e convide professores ou alunos. Quem já tem conta entra com ela e aceita o convite.</p></div>

          {painelQuery.isLoading && <p className="card text-sm text-gray-500">Carregando painel...</p>}
          {painelQuery.isError && <p className="card text-sm text-red-600">{getApiError(painelQuery.error)}</p>}

          {painelQuery.data && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <Metric icon={GraduationCap} label="Turmas" value={painelQuery.data.turmas.length} />
                <Metric icon={Users} label="Pessoas" value={painelQuery.data.membros.length} />
                <Metric icon={Link2} label="Convites pendentes" value={painelQuery.data.convites.length} />
              </div>

              <div className="grid gap-5 xl:grid-cols-2">
                <form className="card space-y-3" onSubmit={event => { event.preventDefault(); criarTurma.mutate(turmaForm) }}>
                  <div className="flex items-center gap-2"><Plus size={17} /><h3 className="font-medium">Nova turma</h3></div>
                  <input className="input-field" placeholder="Nome da turma" value={turmaForm.nome} onChange={event => setTurmaForm({ ...turmaForm, nome: event.target.value })} required />
                  <input className="input-field" placeholder="Descrição (opcional)" value={turmaForm.descricao} onChange={event => setTurmaForm({ ...turmaForm, descricao: event.target.value })} />
                  <input className="input-field" type="number" min="2000" max="2100" value={turmaForm.ano_letivo} onChange={event => setTurmaForm({ ...turmaForm, ano_letivo: Number(event.target.value) })} required />
                  <button className="btn-primary w-full" disabled={criarTurma.isLoading}>{criarTurma.isLoading ? 'Criando...' : 'Criar turma'}</button>
                </form>

                <form className="card space-y-3" onSubmit={event => { event.preventDefault(); criarConvite.mutate(conviteForm) }}>
                  <div className="flex items-center gap-2"><User size={17} /><h3 className="font-medium">Convidar uma pessoa</h3></div>
                  <input className="input-field" type="email" placeholder="E-mail do convidado" value={conviteForm.email} onChange={event => setConviteForm({ ...conviteForm, email: event.target.value })} required />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <select className="input-field" value={conviteForm.papel} onChange={event => setConviteForm({ ...conviteForm, papel: event.target.value })}><option value="aluno">Aluno</option><option value="professor">Professor</option></select>
                    <select className="input-field" value={conviteForm.turma_id} onChange={event => setConviteForm({ ...conviteForm, turma_id: event.target.value })}><option value="">Sem turma</option>{painelQuery.data.turmas.filter(item => item.status === 'ativa').map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}</select>
                  </div>
                  <button className="btn-primary w-full" disabled={criarConvite.isLoading}>{criarConvite.isLoading ? 'Gerando...' : 'Gerar convite'}</button>
                </form>
              </div>

              {painelQuery.data.turmas.some(item => item.status === 'ativa') && painelQuery.data.membros.some(item => item.papel !== 'gestor') && (
                <form className="card space-y-3" onSubmit={event => { event.preventDefault(); incluirParticipante.mutate(participanteForm) }}>
                  <div><h3 className="font-medium">Incluir pessoa em uma turma</h3><p className="mt-1 text-xs text-gray-500">Use esta opção para vincular professores ou alunos que já aceitaram o convite.</p></div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <select className="input-field" value={participanteForm.membro_id} onChange={event => setParticipanteForm({ ...participanteForm, membro_id: event.target.value })} required>
                      <option value="">Escolha uma pessoa</option>
                      {painelQuery.data.membros.filter(item => item.papel !== 'gestor').map(item => <option key={item.id} value={item.id}>{item.usuario.nome} · {papelLabels[item.papel]}</option>)}
                    </select>
                    <select className="input-field" value={participanteForm.turma_id} onChange={event => setParticipanteForm({ ...participanteForm, turma_id: event.target.value })} required>
                      <option value="">Escolha uma turma</option>
                      {painelQuery.data.turmas.filter(item => item.status === 'ativa').map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}
                    </select>
                  </div>
                  <button className="btn-secondary w-full" disabled={incluirParticipante.isLoading}>{incluirParticipante.isLoading ? 'Incluindo...' : 'Incluir na turma'}</button>
                </form>
              )}

              {ultimoConvite && (
                <div className="card border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30">
                  <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">Convite pronto para compartilhar</p>
                  <div className="mt-2 flex gap-2"><input className="input-field min-w-0 flex-1 bg-white" readOnly value={ultimoConvite} /><button type="button" onClick={copiarLink} className="btn-primary inline-flex shrink-0 items-center gap-1.5"><Copy size={15} /> Copiar</button></div>
                </div>
              )}

              <div className="grid gap-5 xl:grid-cols-2">
                <ListSection title="Turmas" empty="Nenhuma turma criada.">
                  {painelQuery.data.turmas.map(turma => <div key={turma.id} className="flex items-center justify-between rounded-xl border border-gray-100 p-3 dark:border-zinc-800"><div><p className="text-sm font-medium">{turma.nome}</p><p className="text-xs text-gray-500">{turma.ano_letivo} · {turma.participantes.length} participante(s)</p></div><GraduationCap size={18} className="text-gray-400" /></div>)}
                </ListSection>

                <ListSection title="Pessoas" empty="Nenhuma pessoa vinculada.">
                  {painelQuery.data.membros.map(membro => <div key={membro.id} className="flex items-center justify-between rounded-xl border border-gray-100 p-3 dark:border-zinc-800"><div className="min-w-0"><p className="truncate text-sm font-medium">{membro.usuario.nome}</p><p className="truncate text-xs text-gray-500">{membro.usuario.email}</p></div><span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-medium dark:bg-zinc-800">{papelLabels[membro.papel]}</span></div>)}
                </ListSection>
              </div>

              <ListSection title="Convites aguardando aceite" empty="Nenhum convite pendente.">
                {painelQuery.data.convites.map(convite => <div key={convite.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 p-3 dark:border-zinc-800"><div className="min-w-0"><p className="truncate text-sm font-medium">{convite.email}</p><p className="text-xs text-gray-500">{papelLabels[convite.papel]}{convite.turma ? ` · ${convite.turma.nome}` : ''}</p></div><button type="button" title="Cancelar convite" onClick={() => cancelarConvite.mutate(convite.id)} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"><X size={16} /></button></div>)}
              </ListSection>
            </>
          )}
        </section>
      )}
    </div>
  )
}

function InstitutionCard({ vinculo }) {
  const instituicao = vinculo.instituicao
  const ativa = instituicao.status === 'ativa'
  return <article className="card space-y-5"><div className="flex items-start justify-between gap-4"><div><p className="text-xs uppercase tracking-wider text-gray-400">{instituicao.tipo.replace('_', ' ')}</p><h2 className="mt-1 text-lg font-medium">{instituicao.nome}</h2><p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">{papelLabels[vinculo.papel]}</p></div><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${ativa ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300'}`}>{statusLabels[instituicao.status]}</span></div>{!ativa ? <div className="flex gap-3 rounded-xl border border-amber-100 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30"><Clock3 className="mt-0.5 shrink-0 text-amber-600" size={19} /><div><p className="text-sm font-medium text-amber-900 dark:text-amber-100">Cadastro recebido</p><p className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-300">A instituição aguarda a análise administrativa. Sua conta individual continua disponível.</p></div></div> : <div className="flex gap-3 rounded-xl bg-emerald-50 p-4 dark:bg-emerald-950/30"><CheckCircle2 className="mt-0.5 shrink-0 text-emerald-600" size={19} /><div><p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">Vínculo ativo</p><p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">Você participa desta instituição como {papelLabels[vinculo.papel].toLowerCase()}.</p></div></div>}</article>
}

function Metric({ icon: Icon, label, value }) {
  return <div className="card flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 dark:bg-zinc-800"><Icon size={19} /></span><div><p className="text-lg font-medium">{value}</p><p className="text-xs text-gray-500">{label}</p></div></div>
}

function ListSection({ title, empty, children }) {
  const items = Array.isArray(children) ? children : [children]
  return <section className="card space-y-3"><h3 className="font-medium">{title}</h3>{items.length && items[0] ? <div className="space-y-2">{children}</div> : <p className="text-sm text-gray-500">{empty}</p>}</section>
}

export default InstituicaoPage
